import { useProductBaseStore } from '@/features/Product/stores/productBase';
import { useProductBundleStore } from '@/features/Product/stores/productBundle';
import { useShopifyCartService } from '@/features/Cart/services/shopifyCart';
import { useBundleBuilder } from '@/features/Product/composables/useBundleBuilder';
import { allUpsells } from '@/features/Product/componentData/productUpsellData';
import type { ProductComplete } from '@/features/Product/types/models';
import type { AddToCartItem } from '@/features/Cart/types/servicesShopifyCart';

const cartUrlParams = ['add', 'atc_browse'] as const;

export const useCartUrlParams = () => {
  const shopifyCart = useShopifyCartService();
  const hasCartUrlParams = () =>
    cartUrlParams.some((param) => new URLSearchParams(window.location.search).has(param));
  const getProductByIdentifier = (identifier: string) => {
    const productBaseStore = useProductBaseStore();
    const productBundleStore = useProductBundleStore();
    const allProducts = [
      ...productBaseStore.completeBaseProducts,
      ...productBundleStore.completeBundleProducts,
    ];
    const normalisedIdentifier = identifier.trim();
    return allProducts.find((product) => {
      const sku = product.attributes?.sku;
      const handle = product.attributes?.handle;
      return (
        sku?.toUpperCase() === normalisedIdentifier.toUpperCase() || handle === normalisedIdentifier
      );
    });
  };

  const getUpsellIdForProduct = (product: ProductComplete): string | undefined => {
    const sku = product.attributes?.sku;
    const handle = product.attributes?.handle;
    const matchingUpsell = allUpsells.find((upsell) => {
      const upsellProduct = upsell.upsellProduct?.product;
      if (!upsellProduct) return false;
      if (typeof upsellProduct === 'string') {
        return upsellProduct === sku || upsellProduct === handle;
      }
      return upsellProduct.attributes?.sku === sku || upsellProduct.attributes?.handle === handle;
    });
    return matchingUpsell?.id;
  };

  const buildCartItem = (
    product: ProductComplete,
    quantity: number,
    choiceGroupSelection?: string,
  ): AddToCartItem => {
    const upsellId = getUpsellIdForProduct(product);
    const properties: Record<string, string> = {};
    if (upsellId) {
      properties._upsell_id = upsellId;
    }
    if (product.bundleDefinition) {
      const hasChoiceGroupSelection = !!choiceGroupSelection;
      let bundleBuilder = useBundleBuilder({
        bundleDefinition: product.bundleDefinition,
        autoSelectDefaults: !hasChoiceGroupSelection,
      });
      let appliedChoiceGroupSelection = false;
      if (choiceGroupSelection) {
        product.bundleDefinition.bundleItems.forEach((item, index) => {
          if (item.itemType !== 'choice_group') return;
          const selectedProduct = item.processedProducts?.find((option) => {
            return (
              option.attributes.sku === choiceGroupSelection ||
              option.attributes.categoryDetails?.typeValue === choiceGroupSelection
            );
          });
          if (!selectedProduct || selectedProduct.attributes.soldOut) return;
          const groupKey = item.groupingName || `group${index}`;
          bundleBuilder.clearGroup(groupKey);
          const added = bundleBuilder.addToGroup(groupKey, selectedProduct, item.quantity || 1);
          if (added) {
            appliedChoiceGroupSelection = true;
          }
        });
      }
      if (hasChoiceGroupSelection && !appliedChoiceGroupSelection) {
        bundleBuilder = useBundleBuilder({
          bundleDefinition: product.bundleDefinition,
          autoSelectDefaults: true,
        });
      }
      const bundleCartObject = bundleBuilder.buildCartObject(product);
      if (!bundleCartObject?.items) {
        throw new Error(`Could not build bundle cart object for ${product.attributes.handle}`);
      }
      return {
        ...bundleCartObject.items,
        quantity,
        properties: {
          ...bundleCartObject.items.properties,
          ...properties,
        },
      };
    }
    return {
      id: product.attributes.variantId,
      quantity,
      ...(Object.keys(properties).length && { properties }),
    };
  };

  const buildCartItemsFromParam = (value: string) => {
    return value.split(',').map((item) => {
      const [productPart, choiceGroupSelection] = item.split('|');
      const [identifier, rawQuantity = '1'] = productPart.split(':');
      const product = getProductByIdentifier(identifier);
      if (!product?.attributes?.variantId) {
        throw new Error(`No product/variant found for URL cart item: ${identifier}`);
      }
      return buildCartItem(product, Number(rawQuantity), choiceGroupSelection);
    });
  };

  const handleCartUrlParams = async () => {
    const urlParams = new URLSearchParams(decodeURI(window.location.search));
    const activeParam = cartUrlParams.find((param) => urlParams.has(param));
    if (!activeParam) return;
    document.documentElement.style.opacity = '0';
    try {
      const paramValue = urlParams.get(activeParam);
      if (!paramValue) return;
      const items = buildCartItemsFromParam(paramValue);
      await shopifyCart.clearCart();
      await shopifyCart.addToCart(items);
      if (activeParam === 'add') {
        window.location.replace('/checkout');
        return;
      }
      document.documentElement.style.opacity = '1';
    } catch (error) {
      console.error('Failed to process cart URL params:', error);
      document.documentElement.style.opacity = '1';
    }
  };

  return {
    handleCartUrlParams,
    hasCartUrlParams,
  };
};
